import {
  Check as CheckIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  LockOutlined as LockOutlineIcon,
} from "@mui/icons-material";
import { CardContent, FormHelperText, Grid, IconButton, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { AsolviDatePicker } from "components/AsolviDatePicker";
import { AsolviTimePicker } from "components/AsolviTimePicker";
import { InvoiceAutocomplete } from "components/job/InvoiceAutocomplete";
import { PromptDialog } from "components/PromptDialog";
import StyledCard from "components/StyledCard";
import { StyledCardActions } from "components/StyledCardActions";
import StyledTextField from "components/StyledTextField";
import {
  intlFormatDateNumeric,
  intlFormatDateTimeNumeric,
  intlFormatPrice,
  toDate,
  toDateString,
} from "helpers";
import technicianSettings from "helpers/technicianSettings";
import { Extra } from "models/extra";
import { ExtraValidationSchema } from "models/validation/ExtraValidationSchema";
import { FC, useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppSelector } from "store";
import { isFlagEnabled } from "store/slices/user.store";
import { ValidationError } from "yup";

const PREFIX = "ExtraCard";

const classes = {
  buttonWrapper: `${PREFIX}-buttonWrapper`,
  error: `${PREFIX}-error`,
  buttonDisabled: `${PREFIX}-buttonDisabled`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  width: "100%",

  [`& .${classes.buttonWrapper}`]: {
    marginTop: "10px",
  },

  [`& .${classes.error}`]: {
    color: theme.palette.error.main,
  },

  [`& .${classes.buttonDisabled}`]: {
    border: `1px solid rgba(0, 0, 0, 0.26)`,
  },
}));

interface ExtraCardProps {
  extra: Extra;
  index: number;
  disabled: boolean;
  onRemove: (index: number) => void;
  onUpdate: (extra: Extra, index: number) => void;
}

export const ExtraCard: FC<ExtraCardProps> = (props) => {
  const { extra, index, disabled, onRemove, onUpdate } = props;
  const regExpDecimal = new RegExp(`^-?\\d*.+\\d*$`);
  const intl = useIntl();
  const [openEdit, setOpenEdit] = useState(false);
  const [confirmationDialog, setConfirmationDialog] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const validationSchema = ExtraValidationSchema();
  const [tempPrice, setTempPrice] = useState<string>((extra.salesPrice || 0).toString());
  const [priceError, setPriceError] = useState<string>("");
  const hasUpdatedServiceJobEndpoint = useAppSelector((s) =>
    isFlagEnabled(s, "UpdatedServiceJobEndpoint"),
  );
  const allowSalesPrice = technicianSettings.syncSalesPrice && hasUpdatedServiceJobEndpoint;
  const allowChangeInvoiceType =
    technicianSettings.allowChangeInvoiceType && hasUpdatedServiceJobEndpoint;

  const editJobPart = useCallback(() => {
    setErrors({});
    setOpenEdit(!openEdit);
  }, [openEdit]);

  const removeExtraFromVisit = useCallback(() => {
    onRemove(index);
  }, [index, onRemove]);

  const editExtra = useCallback(
    (extra: Extra) => {
      const isValid = validationSchema.isValidSync(extra);
      if (isValid) {
        onUpdate(extra, index);
        setErrors({});
      } else {
        try {
          validationSchema.validateSync(extra, { strict: true, abortEarly: false });
        } catch (e: any) {
          let { inner } = e as ValidationError;
          let errors: any = {};
          for (let error of inner) {
            if (!error.path) continue;
            errors[error.path] = error.message;
          }
          setErrors({ ...errors });
        }
      }
    },
    [index, onUpdate, validationSchema],
  );

  const changePriceFromInput = useCallback(
    (value: string) => {
      setTempPrice(value);
      const salesPrice = parseFloat(value);

      if (salesPrice > 0) {
        const changedExtra = {
          ...extra,
          salesPrice,
          salesPriceChangedOnClient: true,
        };
        editExtra(changedExtra);
        setPriceError("");
      } else {
        setPriceError(
          intl.formatMessage({
            id: "general.priceGreaterThanZero",
          }),
        );
      }
    },
    [editExtra, extra, intl],
  );

  const changeInvoiceType = useCallback(
    (value: string) => {
      const changedExtra = {
        ...extra,
        invoiceType: value,
      };
      editExtra(changedExtra);
    },
    [editExtra, extra],
  );

  return (
    <StyledGrid item xs={12} sm={6} lg={4}>
      <StyledCard key={extra.id}>
        <CardContent sx={{ width: "100%" }}>
          <Typography variant="body1" component="h3">
            {extra.type}
          </Typography>
          {extra.isTime ? (
            !openEdit ? (
              <>
                <Typography variant="body2" component="p">
                  <FormattedMessage id="times.startTime" />:{" "}
                  {intlFormatDateTimeNumeric(intl, extra.startTime)}
                </Typography>
                <Typography variant="body2" component="p">
                  <FormattedMessage id="times.endTime" />:{" "}
                  {extra.stopTime && intlFormatDateTimeNumeric(intl, extra.stopTime)}
                </Typography>
              </>
            ) : (
              <Grid container spacing={1} mt={1}>
                <Grid item xs={6}>
                  <AsolviDatePicker
                    error={errors?.startDate}
                    name={`startDate`}
                    label={intl.formatMessage({ id: "times.startDate" })}
                    required
                    value={toDate(extra.startTime)}
                    onChange={(value: Date) => {
                      const changedExtra = {
                        ...extra,
                        startTime: toDateString(value),
                      };
                      editExtra(changedExtra);
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <AsolviTimePicker
                    error={errors?.startTime}
                    name={`startTime`}
                    label={intl.formatMessage({ id: "times.startTime" })}
                    required
                    value={toDate(extra.startTime)}
                    onChange={(value: Date) => {
                      const changedExtra = {
                        ...extra,
                        startTime: toDateString(value),
                      };
                      editExtra(changedExtra);
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <AsolviDatePicker
                    error={errors?.stopDate}
                    name={`stopDate`}
                    label={intl.formatMessage({ id: "times.endDate" })}
                    required
                    value={toDate(extra.stopTime)}
                    onChange={(value: Date) => {
                      const changedExtra = {
                        ...extra,
                        stopTime: toDateString(value),
                      };
                      editExtra(changedExtra);
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <AsolviTimePicker
                    error={errors?.stopTime}
                    name={`stopTime`}
                    label={intl.formatMessage({ id: "times.endTime" })}
                    required
                    value={toDate(extra.stopTime)}
                    onChange={(value: Date) => {
                      const changedExtra = {
                        ...extra,
                        stopTime: toDateString(value),
                      };
                      editExtra(changedExtra);
                    }}
                  />
                </Grid>
                {allowChangeInvoiceType && (
                  <Grid item container xs={12}>
                    <InvoiceAutocomplete
                      onChange={changeInvoiceType}
                      value={extra.invoiceType ?? null}
                    />
                  </Grid>
                )}
              </Grid>
            )
          ) : !openEdit ? (
            <>
              {hasUpdatedServiceJobEndpoint && (
                <Typography variant="body2" component="p">
                  {extra.description}
                </Typography>
              )}
              <Typography variant="body2" component="p">
                <FormattedMessage id="times.startTime" />:{" "}
                {intlFormatDateNumeric(intl, extra.startTime)}
              </Typography>
              <Typography variant="body2" component="p">
                <FormattedMessage id="general.quantity" />: {extra.quantity}
              </Typography>
              {allowSalesPrice && (
                <Typography variant="body2" component="p">
                  <FormattedMessage id="general.salesPrice" />:{" "}
                  {intlFormatPrice(intl, extra.salesPrice ?? 0)}
                </Typography>
              )}
            </>
          ) : (
            <Grid container spacing={1} mt={1}>
              {hasUpdatedServiceJobEndpoint && (
                <Grid item container xs={12}>
                  <StyledTextField
                    fullWidth
                    value={extra.description}
                    id="description"
                    label={intl.formatMessage({ id: "general.description" })}
                    onChange={(e: any) => {
                      const { value } = e.target;

                      const changedExtra = {
                        ...extra,
                        description: value,
                      };
                      editExtra(changedExtra);
                    }}
                    multiline
                    rows={5}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <AsolviDatePicker
                  error={errors?.startDate}
                  name={`startDate`}
                  label={intl.formatMessage({ id: "times.startDate" })}
                  required
                  value={toDate(extra.startTime)}
                  onChange={(value: Date) => {
                    const changedExtra = {
                      ...extra,
                      startTime: toDateString(value),
                    };
                    editExtra(changedExtra);
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <StyledTextField
                  error={!!errors?.Quantity}
                  inputProps={{ step: "any" }}
                  type="number"
                  name="quantity"
                  label={intl.formatMessage({ id: "general.quantity" })}
                  value={extra.quantity}
                  style={{ marginTop: "8px" }}
                  onChange={(e: any) => {
                    const { value } = e.target;

                    if (regExpDecimal.test(value) || value === "") {
                      const changedExtra = {
                        ...extra,
                        quantity: Number(value),
                      };
                      editExtra(changedExtra);
                    }
                  }}
                />
                <FormHelperText error>{errors?.Quantity}</FormHelperText>
              </Grid>
              {allowSalesPrice && (
                <Grid item container xs={12}>
                  <StyledTextField
                    value={tempPrice}
                    id="salesPrice"
                    type="number"
                    label={intl.formatMessage({ id: "general.salesPrice" })}
                    onChange={(event) => changePriceFromInput(event.target.value)}
                    helperText={priceError}
                    error={priceError !== ""}
                  />
                </Grid>
              )}
              {allowChangeInvoiceType && (
                <Grid item container xs={12}>
                  <InvoiceAutocomplete
                    onChange={changeInvoiceType}
                    value={extra.invoiceType ?? null}
                  />
                </Grid>
              )}
            </Grid>
          )}
        </CardContent>
        <StyledCardActions>
          {disabled && (
            <IconButton disabled>
              <LockOutlineIcon color="primary" />
            </IconButton>
          )}
          {!disabled && (
            <>
              <IconButton
                aria-label={intl.formatMessage({ id: "general.delete" })}
                onClick={() => setConfirmationDialog(true)}
                size="large"
              >
                <DeleteIcon color="error" />
              </IconButton>
              {openEdit ? (
                <IconButton
                  aria-label={intl.formatMessage({ id: "general.save" })}
                  onClick={editJobPart}
                  size="large"
                >
                  <CheckIcon color="primary" />
                </IconButton>
              ) : (
                <IconButton
                  aria-label={intl.formatMessage({ id: "general.edit" })}
                  onClick={editJobPart}
                  size="large"
                >
                  <EditIcon color="action" />
                </IconButton>
              )}
            </>
          )}
        </StyledCardActions>
      </StyledCard>
      <PromptDialog
        aria-labelledby="form-dialog-title"
        open={confirmationDialog}
        setOpen={setConfirmationDialog}
        okText={intl.formatMessage({ id: "general.confirm" })}
        onOk={removeExtraFromVisit}
        title={<FormattedMessage id="visit.extra.remove" />}
        promptContent={<FormattedMessage id="visit.extra.removePrompt" />}
      />
    </StyledGrid>
  );
};
